<template>
  <div>
    <b-alert
      v-if="! hasCoordinates && hasGeocoderFailed"
      :show="true"
      variant="danger"
    >
      Het opgegeven adres leverde geen locatie op binnen de postcode <strong>{{ postalcode }}</strong>. Controleer of het adres juist is ingevoerd, of voer handmatig coordinaten in.
    </b-alert>
    <b-alert
      v-else-if="! hasCoordinates && ! hasGeocoderBeenActivated"
      :show="true"
      variant="info"
    >
      De coordinaten van de locatie worden momenteel opgehaald. Als deze melding niet verdwijnt kan er iets mis zijn gegaan. Gebruik dan de onderstaande knop.

      <b-button
        class="mt-2"
        @click="handleRunGeocoder"
      >
        Start geocoder handmatig
      </b-button>
    </b-alert>
    <Coordinates
      v-if="isUserCPO"
      :values="coordinatesValues"
      :request="request"
      :disabled="isCoordinatesDisabled"
      @pin-location="handlePinLocation"
      @coordinates="handleLocationCoordinates"
    />
    <b-aspect :aspect="aspect">
      <MapBox
        :access-token="accessToken"
        :map-style.sync="mapStyle"
        :options="mapOptions"
        @load="onMapLoaded"
      >
        <LayerRealisationProcesses
          v-if="! isGORAL"
          :key="`LayerRealisationProcesses-${componentKey}`"
          @click="handleOpenRealisationPopup"
        />

        <LayerRequests
          :uuid="uuid"
          :coordinates="coordinatesValues"
        />

        <LayerChargingpoints
          :code="geographyCode"
          :focus="true"
        />

        <PopupRequest :record="{}" />

        <PopupRealisationProcess
          v-if="! isGORAL"
          @close="handleCloseRealisationProcessPopup"
        />

        <PopupChargingLocation />
      </MapBox>
    </b-aspect>

    <LegendRequest />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

import MapBox from '@/components/common/MapBox.vue'
import LayerChargingpoints from '@/components/map/LayerChargingpoints.vue'
import LayerRequests from '@/components/map/LayerRequests.vue'
import LayerRealisationProcesses from '@/components/map/LayerRealisationProcesses.vue'
import PopupRealisationProcess from '@/components/map/PopupRealisationProcess.vue'
import PopupRequest from '@/components/map/PopupRequest.vue'
import PopupChargingLocation from '@/components/map/PopupChargingLocation.vue'
import Coordinates from '@/components/definitions/Coordinates.vue'

import { formatPostalCode } from '@/helpers/string'

import LegendRequest from '@/components/map/LegendRequest.vue'

import { IconButtonControl } from '@/services/mapbox'

import { monitoringConfig, mapOptions } from '@/config'

/**
 * Shows
 *  - the active Request location,
 *  (- the other Requests)
 *  - the existing chargingpoints
 */
export default {
  name: 'MapRequest',
  components: {
    MapBox, LayerChargingpoints, LayerRequests, LayerRealisationProcesses,
    PopupRealisationProcess, PopupChargingLocation, PopupRequest,
    LegendRequest, Coordinates,
  },
  // TODO: Remove dependency injection
  provide () {
    return {
      registerFormField: null,
      unregisterFormField: null,
    }
  },
  props: {
    /**
     * The Request Uuid
     */
    uuid: {
      type: String,
      required: true,
    },
    aspect: {
      type: String,
      default: '16:10',
    },
  },
  data() {
    return {
      /**
       * MapBox instance
       */
      map: null,

      /**
       * MapBox SDK reference
       */
      mapbox: null,

      /**
       * MapBox is ready
       */
      loaded: false,

      /**
       * MapBox config
       */
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,

      /**
       * A default Mapbox Marker on the Request address position
       */
      requestMarker: null,

      /**
       * MapBox geocoder field
       */
      geocoder: null,

      /**
       * The uuid of the clicked realisation process
       */
      realisationUuid: null,

      /**
       * Reference to icon btn control
       */
      iconBtnControl: null,

      /**
       * Whether the tenant has purchased the monitoring feature
       */
      hasMonitoring: !! monitoringConfig.enabled,
      componentKey: 0,
      isCoordinatesDisabled: false,
      saveRequestError: false,
      coordinatesValues: {
        Longitude: '',
        Latitude: '',
      },
    }
  },
  computed: {
    ...mapGetters('requests', ['requestByUuid']),
    ...mapGetters('tenant', [
      'isCurrentTenant',
    ]),
    ...mapGetters('user', ['hasRole']),
    request() {
      return this.requestByUuid({ uuid: this.uuid })
    },
    postalcode() {
      return this.request && this.request.address && this.request.address.postalCode
        ? formatPostalCode(this.request.address.postalCode)
        : '-'
    },
    /**
     * Whether the loaded request is lacking coordinates
     */
    hasCoordinates() {
      return !! this.request?.hasCoordinates()
    },

    hasGeocoderFailed() {
      return !! this.request.hasGeocoderFailed()
    },
    hasGeocoderBeenActivated() {
      return !! this.request.hasGeocoderBeenActivated()
    },

    /**
     * Get the Geography code, use to show Charging Point Locations
     */
    geographyCode() {
      return this.request?.getGeographyCode() || null
    },

    noMatch() {
      return this.request?.coordinates?.nomatch
    },
    mapOptions() {
      const center = this.hasCoordinates
        ? [this.request.coordinates.lng, this.request.coordinates.lat]
        : (mapOptions.center || [4.9041, 52.3676])

      return {
        center,
        zoom: this.hasCoordinates ? 14 : (mapOptions.zoom || 10),
      }
    },
    isGORAL() {
      return this.isCurrentTenant({ tenant: 'go-ral' })
    },

    isUserCPO () {
      return this.hasRole({ role: 'cpo' })
    },
  },
  watch: {
    '$route': {
      handler () {
        this.componentKey++
      },
    },
  },
  created () {
    this.coordinatesValues.Longitude = this.request.coordinates.lng
    this.coordinatesValues.Latitude = this.request.coordinates.lat
  },

  beforeDestroy() {
    if (this.requestMarker) {
      this.requestMarker.remove()
    }
  },
  methods: {
    ...mapMutations('requests', [
      'replaceRequest',
    ]),
    onMapLoaded({ map, mapbox }) {
      this.loaded = true
      this.map = map
      this.mapbox = mapbox

      // Add the GeoCoder plugin
      // This geocoder is only for navigation purposes
      // License restrictions prevent other usage
      this.geocoder = new MapboxGeocoder({
        accessToken: this.accessToken,
        mapboxgl: this.mapbox,

        countries: mapOptions.countries || 'nl',
        types: 'address',
        language: 'nl',

        collapsed: true,
        placeholder: 'Zoek op adres',
        marker: false,

      })
      this.map.addControl(
        this.geocoder,
      )

      if (this.hasMonitoring) {
        this.iconBtnControl = new IconButtonControl({
          callback: this.handleIconBtn,
        })
        this.map.addControl(
          this.iconBtnControl,
        )
      }

      if (this.hasCoordinates) {
        this.addMarker()
      }
    },
    addMarker() {
      if (this.requestMarker) return

      // // Add the Request as a regular MapBox Marker
      // this.requestMarker = new this.mapbox.Marker({
      //   'color': '#dc3545'
      // })
      //   .setLngLat(this.mapOptions.center)
      //   .addTo(this.map)
    },
    handleOpenRealisationPopup({ uuid }) {
      this.realisationUuid = uuid
    },
    handleCloseRealisationProcessPopup() {
      this.realisationUuid = null
    },

    handleIconBtn() {
      this.$emit('open')
    },

    handlePinLocation (event) {
      this.coordinatesValues.Longitude = event.lng
      this.coordinatesValues.Latitude = event.lat

      this.map.flyTo({
        center: event,
        zoom: 14,
        duration: 2000,
        essential: true,
      })
    },

    async handleRunGeocoder() {
      this.isCoordinatesDisabled = true
      this.saveRequestError = false

      try {
        let response = await this.$cbapi.requests.runGeocoder({
          uuid: this.request.getUuid(),
        })
        if (response !== false) {
          this.replaceRequest({
            request: response.data,
          })
        } else {
          throw new Error('Failed')
        }

      } catch (e) {
        this.saveRequestError = true
      } finally {
        this.isCoordinatesDisabled = false
        const message = this.saveRequestError
          ? 'Er is iets fout gegaan!'
          : 'De geocoder is klaar!'

        this.notification({ message })
      }
    },

    async handleLocationCoordinates (event) {
      this.isCoordinatesDisabled = true
      this.saveRequestError = false

      try {
        // TODO: CBAPI - TEST
        let response = await this.$cbapi.requests.setCoordinates({
          uuid: this.request.getUuid(),
          coordinates: event,
        })

        if (response == false) {
          throw new Error('failed')
        }

        this.replaceRequest({
          request: response.data,
        })

      } catch (error) {
        this.saveRequestError = true

      } finally {
        this.isCoordinatesDisabled = false
        const message = this.saveRequestError
          ? 'Er is iets fout gegaan!'
          : 'Coördinaten zijn opgeslagen!'

        this.notification({ message })
      }
    },

    notification ({ message }) {
      this.$bvToast.toast(message, {
        title: this.saveRequestError ? 'Fout!' : 'Succes!',
        variant: this.saveRequestError ? 'danger' : 'success',
        toaster: 'b-toaster-top-right',
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss">
.Legend__title {
  display: block;
  padding-top: 1.5rem;
}
.Legend__list {
  list-style-type: none;
  padding: 0.5rem 0 0;
  margin: 0;

  li {
    margin-top: 0.5rem;

    div {
      display: inline-block;
      margin-left: 0.5rem;
    }

  }
}

.IconBtn {
  position: relative;
  box-shadow: 0 0 10px 2px rgba(0,0,0,.1);
  background: white;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  color: #757575;

  > svg {
    position: absolute;
    top: 8px;
    left: 8px;
  }

  &:hover {
    color: black;
  }
}
</style>
